import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut, Warning, ShieldCheck, Info } from "@phosphor-icons/react";
import api, { scoreColor } from "../lib/api";
import CompetitionCard from "../components/CompetitionCard";
import { ScoreBar } from "../components/ScoreBreakdown";
import AffiliateLinkLabel from "../components/AffiliateLinkLabel";
import { affiliateDisclosureFor } from "../lib/affiliateDisclosure";
import SEO from "../components/SEO";

const VERIFICATION_COPY = {
  verified: {
    icon: ShieldCheck,
    tone: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
    label: "Reviewed by Competition Checker",
    body: "Operator transparency metadata reviewed by Competition Checker.",
  },
  partial: {
    icon: Info,
    tone: "bg-amber-500/10 border-amber-500/30 text-amber-200",
    label: "Partial review",
    body: "Some operator details have been captured, but this record still needs review.",
  },
  seed_candidate: {
    icon: Warning,
    tone: "bg-slate-500/15 border-slate-500/30 text-slate-200",
    label: "Pending review",
    body: "Operator record pending manual verification. Treat this listing as preliminary.",
  },
  archived: {
    icon: Info,
    tone: "bg-slate-500/10 border-slate-500/20 text-slate-300",
    label: "Archived",
    body: "This operator is archived and no longer actively reviewed.",
  },
};

const TYPE_LABEL = {
  operator: null,
  directory: "Directory site",
  affiliate_site: "Affiliate site",
  review_site: "Review site",
  platform: "Hosted platform",
  unknown: "Type unconfirmed",
};

const CoverageBanner = ({ op }) => {
  if (!op.public_status && !op.coverage_status && !op.coverage_note) return null;
  const features = op.coverage_features || {};
  const chips = [
    features.live_coverage && "Live coverage",
    features.trusted_automation && "Trusted automation",
    features.scheduled_refresh && "Scheduled refresh",
    features.odds_ready_support && "Standard draws supported",
    features.expiry_supported && "Expiry supported",
  ].filter(Boolean);
  return (
    <div
      data-testid="operator-coverage-banner"
      className="mt-5 rounded-lg border border-[var(--cc-gold)]/30 bg-[var(--cc-gold)]/10 px-4 py-3"
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--cc-gold)]">
        {op.public_status || "Coverage"}{op.coverage_status ? ` / ${op.coverage_status}` : ""}
      </div>
      {op.coverage_note && (
        <p className="mt-1 text-xs leading-relaxed text-white/80">{op.coverage_note}</p>
      )}
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/75"
            >
              {chip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const VerificationBanner = ({ op }) => {
  const meta = VERIFICATION_COPY[op.operator_verification_status || "seed_candidate"];
  const typeLabel = TYPE_LABEL[op.operator_type];
  const Icon = meta.icon;
  return (
    <div
      data-testid="operator-verification-banner"
      className={`mt-5 rounded-lg border px-4 py-3 ${meta.tone} flex items-start gap-3`}
    >
      <Icon size={18} weight="fill" className="flex-none mt-0.5" />
      <div className="text-xs leading-relaxed">
        <div className="font-semibold uppercase tracking-wider text-[11px] mb-0.5">
          {meta.label}
          {typeLabel && <span className="opacity-70 normal-case font-normal"> · {typeLabel}</span>}
        </div>
        <div className="opacity-90">{meta.body}</div>
      </div>
    </div>
  );
};

export default function OperatorDetail() {
  const { slug } = useParams();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/operators/${slug}`)
      .then((r) => setOp(r.data), () => setOp(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="max-w-7xl mx-auto p-12 text-[var(--cc-text-muted)]">
      <SEO
        title="Loading operator profile"
        description="Competition Checker is loading this public operator profile."
        path={`/operators/${slug}`}
        noIndex
      />
      Loading...
    </div>
  );
  if (!op) return (
    <div className="max-w-7xl mx-auto p-12 text-center">
      <SEO
        title="Operator not found"
        description="The requested public operator profile could not be found."
        path={`/operators/${slug}`}
        noIndex
      />
      <h1 className="font-display text-3xl font-semibold">Operator not found</h1>
      <p className="mt-3 text-[var(--cc-text-muted)]">
        This operator profile could not be found or is no longer available.
      </p>
      <Link className="mt-4 inline-flex text-[var(--cc-gold)] hover:underline" to="/operators">
        Browse operators
      </Link>
    </div>
  );
  const operatorAffiliateDisclosure = affiliateDisclosureFor(op, op.website_url);
  const seoDescription = `${op.name} transparency profile, trust signals and active UK prize competitions on Competition Checker.`;
  const operatorCanonical = `https://competitionchecker.com/operators/${op.slug || slug}`;
  const breadcrumbId = `${operatorCanonical}#breadcrumb`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${operatorCanonical}#webpage`,
        url: operatorCanonical,
        name: `${op.name} transparency and competition profile`,
        description: seoDescription,
        isPartOf: { "@id": "https://competitionchecker.com/#website" },
        breadcrumb: { "@id": breadcrumbId },
        about: { "@type": "Organization", name: op.name },
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbId,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Operators", item: "https://competitionchecker.com/operators" },
          { "@type": "ListItem", position: 2, name: op.name, item: operatorCanonical },
        ],
      },
    ],
  };
  const competitionCount = op.competitions?.length || 0;
  const oddsReadyCount = op.competitions?.filter((competition) => competition.odds_ready === true).length || 0;
  const oddsUnavailableCount = competitionCount - oddsReadyCount;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <SEO
        title={`${op.name} transparency and competition profile`}
        description={seoDescription}
        path={`/operators/${op.slug || slug}`}
        jsonLd={jsonLd}
        jsonLdId={`operator-${op.slug || slug}`}
      />
      <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs text-white/60">
        <Link to="/operators" className="inline-flex items-center gap-2 hover:text-white">
          <ArrowLeft size={14} /> Operators
        </Link>
        <span aria-hidden="true">/</span>
        <span>{op.name}</span>
      </nav>

      <div className="grid lg:grid-cols-[1fr_320px] gap-10 items-start">
        <div>
          <div className="overline text-[var(--cc-gold)] mb-2">Operator profile</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">{op.name}</h1>
          <p className="text-[var(--cc-text-muted)] mt-3 leading-relaxed">{op.trust_summary}</p>
          <p className="mt-3 text-sm leading-relaxed text-white/65">
            Competition Checker currently tracks {competitionCount} public competition{competitionCount === 1 ? "" : "s"} for this operator:
            {` ${oddsReadyCount} odds-ready and ${oddsUnavailableCount} with odds unavailable.`}
            Directory membership means the operator identity is tracked; it is not an endorsement or safety certification.
          </p>
          <CoverageBanner op={op} />
          <VerificationBanner op={op} />
          <a
            href={op.website_url}
            target="_blank"
            rel={operatorAffiliateDisclosure.isAffiliateLink ? "sponsored noopener noreferrer" : "noopener noreferrer"}
            data-testid="operator-website-link"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-[var(--cc-gold)] hover:underline"
          >
            Visit operator website <ArrowSquareOut size={14} />
          </a>
          <div className="mt-2">
            <AffiliateLinkLabel
              record={op}
              destinationUrl={op.website_url}
              testId="operator-affiliate-label"
              className="bg-white/10 text-amber-100 border-amber-300/50"
            />
          </div>

          <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Operator profile help">
            <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">How operator signals are scored</Link>
            <Link className="text-[var(--cc-gold)] hover:underline" to="/data-accuracy">Data accuracy</Link>
            <Link className="text-[var(--cc-gold)] hover:underline" to="/affiliate-disclosure">Commercial independence</Link>
          </nav>

          <div className="mt-8 rounded-lg bg-white p-6 border border-slate-200">
            <div className="overline text-[var(--cc-text-dark-muted)] mb-4">Transparency breakdown</div>
            <div className="space-y-4">
              <ScoreBar value={op.winner_visibility_score} label="Winner visibility" />
              <ScoreBar value={op.terms_clarity_score} label="Terms clarity" />
              <ScoreBar value={op.free_entry_clarity_score} label="Free entry clarity" />
              <ScoreBar value={op.extension_policy_clarity_score} label="Extension policy clarity" />
            </div>
            {op.social_proof_summary && (
              <p className="mt-5 pt-5 border-t border-slate-200 text-sm text-[var(--cc-text-dark-muted)] leading-relaxed">
                {op.social_proof_summary}
              </p>
            )}
          </div>

          {op.known_risk_flags?.length > 0 && (
            <div className="mt-6 rounded-lg p-5 bg-red-50 border border-red-200">
              <div className="overline text-red-800 mb-3 inline-flex items-center gap-1.5"><Warning size={12} weight="fill"/> Known risk flags</div>
              <ul className="space-y-1.5">
                {op.known_risk_flags.map((f) => (
                  <li key={f} className="text-sm text-red-900 flex gap-2">
                    <span>•</span><span>{f.replace(/_/g, " ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="rounded-lg bg-white p-6 border border-slate-200">
          <div className="overline text-[var(--cc-text-dark-muted)]">Transparency score</div>
          <div className="font-display text-5xl font-semibold mt-2 font-mono-num" style={{ color: scoreColor(op.trust_score) }}>
            {op.trust_score}<span className="text-2xl text-[var(--cc-text-dark-muted)]">/100</span>
          </div>
          <p className="text-xs text-[var(--cc-text-dark-muted)] mt-3 leading-relaxed">
            Composite transparency signal from winner visibility, terms clarity,
            free-entry clarity and extension-policy clarity.
          </p>
        </aside>
      </div>

      <div className="mt-16">
        <h2 className="font-display text-2xl font-semibold mb-6">Active competitions</h2>
        {op.competitions?.length === 0 ? (
          <div
            data-testid="operator-no-comps-banner"
            className="rounded-lg border border-white/15 bg-white/[0.03] p-6"
          >
            <div className="font-display text-lg font-semibold text-white mb-1">
              Operator captured. Competitions not yet listed.
            </div>
            <p className="text-sm text-[var(--cc-text-muted)] leading-relaxed max-w-2xl">
              We have added this operator to the Competition Checker review queue,
              but active competitions have not been captured yet. This does not
              mean the operator is inactive — it means our editorial team has not
              yet verified live listings for them.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {op.competitions.map((c, i) => <CompetitionCard key={c.id} comp={c} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
